import React, { useEffect, useMemo } from 'react';
import { useSortBy, useTable } from 'react-table';
import {
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  Select,
} from '@chakra-ui/react';
import Card from '../../../../components/card/Card';
import ApplicationsApi, { Applications, ApplicationStatus } from '../../../../api/applications';
import {
  tableColumns,
  TagActions,
  TagAmount,
  TagBranchName,
  TagDate,
  TagGovId,
  TagName,
  TagStatus,
} from './applicationsTableData';
import {
  getEndDate,
  matchDateFormatToBackend,
  prettifyDate,
} from '../../../../functions/dateUtils';
import User from '../../../../api/user';
import MonthDropdownSelection from '../../../../functions/MonthDropdownSelection';
import { exportArrayToXlsxFile } from '../../../../functions/generatePayrollFile';

// Compute "DD/MM/YYYY" boundaries for an Xtras pay period.
// "11/2026" → ["26/10/2026", "25/11/2026"] (Oct 26 → Nov 25 inclusive).
function xtrasRangeForPayPeriod(payPeriodString) {
  const [sMonth, sYear] = payPeriodString.split('/');
  const endMonth = parseInt(sMonth, 10);
  const endYear = parseInt(sYear, 10);
  const startMonth = endMonth === 1 ? 12 : endMonth - 1;
  const startYear = endMonth === 1 ? endYear - 1 : endYear;
  const pad = (n) => String(n).padStart(2, '0');
  return [`26/${pad(startMonth)}/${startYear}`, `25/${pad(endMonth)}/${endYear}`];
}

// Current open Xtras pay period as "MM/YYYY". On or before the 25th → this month;
// after the 25th → next month (we've rolled into the next cycle).
function currentXtrasPayPeriod() {
  const today = new Date();
  const day = today.getDate();
  const month = today.getMonth() + 1;
  const year = today.getFullYear();
  const pad = (n) => String(n).padStart(2, '0');
  if (day <= 25) return `${pad(month)}/${year}`;
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  return `${pad(nextMonth)}/${nextYear}`;
}

export default function TransfersTable({ selectedCompanyId }) {
  // Xtras (companyId 43) view transfers in the 26→25 pay cycle instead of calendar months.
  // Gate on type-2 + companyId 43 so Payro superadmins viewing globally stay on calendar logic.
  const initialUserInfo = User.GetInfo();
  const isXtrasAdmin = !!(
    initialUserInfo &&
    Number(initialUserInfo.type) === 2 &&
    Number(initialUserInfo.companyId) === 43
  );
  const defaultXtrasPayPeriod = isXtrasAdmin ? currentXtrasPayPeriod() : '';
  let columns = useMemo(() => tableColumns, [tableColumns]);
  const [data, setData] = React.useState([]);
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');
  const [applicationStatus, setApplicationStatus] = React.useState('');
  const [selectedApplication, setSelectedApplication] = React.useState(undefined);
  const [userType, setUserType] = React.useState(-1);
  const [isExportingPdfs, setIsExportingPdfs] = React.useState(false);

  const fetchForMonth = (month, year, status, companyId) => {
    const start = `01/${month}/${year}`;
    const end = getEndDate(month, year);
    return ApplicationsApi.getAll(status, start, end, companyId || null)
      .then((res) => res?.data || []);
  };

  const refreshApplications = (overrideStart, overrideEnd) => {
    const s = overrideStart !== undefined ? overrideStart : startDate;
    const e = overrideEnd !== undefined ? overrideEnd : endDate;
    ApplicationsApi.getAll(applicationStatus, s, e, selectedCompanyId || null)
      .then((res) => {
        if (res && res.data) {
          try {
            res.data.sort(function (a, b) {
              return new Date(b['createdAt']) - new Date(a['createdAt']);
            });
          } catch (e) {}
        }
        setData(res.data);
      })
      .catch(() => setData([]));
  };

  const loadDefaultMonth = async () => {
    // Xtras admins: load the currently-open 26→25 period directly. If empty, fall back
    // to the previous closed Xtras period so the view isn't blank on month boundaries.
    if (isXtrasAdmin) {
      const [curStart, curEnd] = xtrasRangeForPayPeriod(defaultXtrasPayPeriod);
      let results = (
        await ApplicationsApi.getAll(applicationStatus, curStart, curEnd, selectedCompanyId || null)
      )?.data || [];
      setStartDate(curStart);
      setEndDate(curEnd);
      if (!results.length) {
        // Compute the previous Xtras pay period by walking the "MM/YYYY" back one month.
        const [sMonth, sYear] = defaultXtrasPayPeriod.split('/');
        const curPM = parseInt(sMonth, 10);
        const curPY = parseInt(sYear, 10);
        const prevPM = curPM === 1 ? 12 : curPM - 1;
        const prevPY = curPM === 1 ? curPY - 1 : curPY;
        const prevPeriod = `${String(prevPM).padStart(2, '0')}/${prevPY}`;
        const [prevStart, prevEnd] = xtrasRangeForPayPeriod(prevPeriod);
        results = (
          await ApplicationsApi.getAll(applicationStatus, prevStart, prevEnd, selectedCompanyId || null)
        )?.data || [];
        setStartDate(prevStart);
        setEndDate(prevEnd);
      }
      try { results.sort((a, b) => new Date(b['createdAt']) - new Date(a['createdAt'])); } catch {}
      setData(results);
      return;
    }

    const now = new Date();
    const curMonth = String(now.getMonth() + 1).padStart(2, '0');
    const curYear = String(now.getFullYear());

    let results = await fetchForMonth(curMonth, curYear, applicationStatus, selectedCompanyId);

    if (!results.length) {
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const prevMonth = String(prev.getMonth() + 1).padStart(2, '0');
      const prevYear = String(prev.getFullYear());
      results = await fetchForMonth(prevMonth, prevYear, applicationStatus, selectedCompanyId);
      setStartDate(`01/${prevMonth}/${prevYear}`);
      setEndDate(getEndDate(prevMonth, prevYear));
    } else {
      setStartDate(`01/${curMonth}/${curYear}`);
      setEndDate(getEndDate(curMonth, curYear));
    }

    try { results.sort((a, b) => new Date(b['createdAt']) - new Date(a['createdAt'])); } catch {}
    setData(results);
  };

  useEffect(() => {
    setUserType(User.GetInfo().type);
    loadDefaultMonth();
  }, [selectedCompanyId]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    { columns, data },
    useSortBy,
  );

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const {
    isOpen: isExportModalOpen,
    onOpen: onExportModalOpen,
    onClose: onExportModalClose,
  } = useDisclosure();

  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: onEditModalClose,
  } = useDisclosure();

  const handleStartDate = (event) => {
    const value = event.target.value;
    setStartDate(matchDateFormatToBackend(value));
  };

  const handleEndDate = (event) => {
    const value = event.target.value;
    setEndDate(matchDateFormatToBackend(value));
  };

  const setStartAndEndDate = (selectedMonth) => {
    if (isXtrasAdmin) {
      const [xStart, xEnd] = xtrasRangeForPayPeriod(selectedMonth);
      setStartDate(xStart);
      setEndDate(xEnd);
      return;
    }
    const [month, year] = selectedMonth.split('/');
    const startDate = `01/${month}/${year}`;
    const endDate = getEndDate(month, year);
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const handleRefreshClick = (event) => {
    refreshApplications();
  };

  const handleExportApplicationsClick = () => {
    onExportModalOpen();
  };

  const handleExportAllPdfsClick = async () => {
    const eligibleApps = data.filter(
      (app) => app.status === 500 || app.status === 700,
    );
    if (eligibleApps.length === 0) {
      alert('אין אישורים להורדה עבור הסינון הנוכחי');
      return;
    }
    setIsExportingPdfs(true);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      for (const app of eligibleApps) {
        const buffer = await ApplicationsApi.fetchApprovalPdfBuffer(app.id);
        const filename = `approval-${app.id}-${(app.fullName || '').replace(/\s+/g, '_')}.pdf`;
        zip.file(filename, buffer);
      }
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `approvals-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to export PDFs', e);
      alert('שגיאה בייצוא הקבצים');
    } finally {
      setIsExportingPdfs(false);
    }
  };

  const handleExportToExcelClick = () => {
    const filteredData = data.map((row) => ({
      companyName: row.companyName,
      fullName: row.fullName,
      branch: row.branch || '',
      amount: row.amount,
      createdAt: row.createdAt,
    }));
    exportArrayToXlsxFile(filteredData);
  };

  const handleStatusChange = (event) => {
    const value = event.target.value;
    setApplicationStatus(value);
  };

  const handleEditClick = (cellRow) => {
    setSelectedApplication(data[cellRow.index]);
    onEditModalOpen();
  };

  const handleDeleteConfirm = () => {};

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
      <Flex px="25px" mb="20px" align="center">
        <MonthDropdownSelection
          handleSelectedMonth={setStartAndEndDate}
          value={isXtrasAdmin ? defaultXtrasPayPeriod : undefined}
          showXtrasSubtitle={isXtrasAdmin}
        />

        <Select marginRight="30px" width="190px" pr="15px" onChange={handleStatusChange}>
          <option value="88" selected disabled hidden>
            בחר סטטוס העברה
          </option>
          <option value="">הכל</option>
          <option value={ApplicationStatus.Canceled}>בוטל</option>
          <option value={ApplicationStatus.Declined}>נדחה</option>
          <option value={ApplicationStatus.PendingTransfer}>ממתין להעברה</option>
          <option value={ApplicationStatus.Transferred}>הועבר</option>
          <option value={ApplicationStatus.Completed}>הושלם</option>
        </Select>

        <Button marginRight="30px" width="80px" variant={'brand'} onClick={handleRefreshClick}>
          הצג
        </Button>
      </Flex>

      <Flex px="0px" mb="20px" align="center">
        <Button
          marginRight="30px"
          width="150px"
          variant="blue"
          isDisabled={data.length === 0}
          onClick={handleExportToExcelClick}
          title={data.length === 0 ? 'אין העברות לייצוא' : ''}
        >
          ייצוא לאקסל
        </Button>

        <Button
          marginRight="30px"
          width="170px"
          variant="blue"
          onClick={handleExportAllPdfsClick}
          isLoading={isExportingPdfs}
          loadingText="מייצא..."
        >
          ייצוא אישורי העברות
        </Button>
      </Flex>

      <Table {...getTableProps()} variant="simple" color="gray.500" mb="24px">
        <Thead>
          {headerGroups.map((headerGroup, index) => (
            <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
              {headerGroup.headers.map((column, index) => (
                <Th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  pe="10px"
                  key={index}
                  borderColor={borderColor}
                >
                  <Flex
                    justify="space-between"
                    align="center"
                    fontSize={{ sm: '10px', lg: '12px' }}
                    color="gray.400"
                  >
                    {column.render('Header')}
                  </Flex>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {rows.map((row, index) => {
            prepareRow(row);
            return (
              <Tr {...row.getRowProps()} key={index}>
                {row.cells.map((cell, index) => {
                  let data = '';
                  let cellWidth = cell.column.width;
                  if (cell.column.tag === TagAmount) {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        ₪{Number(cell.value).toLocaleString('en-US')}
                      </Text>
                    );
                  } else if (
                    cell.column.tag === TagName ||
                    cell.column.tag === TagGovId ||
                    cell.column.tag === TagBranchName
                  ) {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {cell.value}
                      </Text>
                    );
                  } else if (cell.column.tag === TagStatus) {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {Applications.statusText(cell.value)}
                      </Text>
                    );
                  } else if (cell.column.tag === TagDate) {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {prettifyDate(cell.value)}
                      </Text>
                    );
                  } else if (cell.column.tag === TagActions) {
                    data = (
                      <Flex align="center">
                        <Button
                          onClick={() => handleEditClick(cell.row)}
                          marginLeft="20px"
                          textColor="black"
                          backgroundColor="green.200"
                          height="35px"
                        >
                          עריכה
                        </Button>
                      </Flex>
                    );
                  }
                  return (
                    <Td
                      {...cell.getCellProps()}
                      key={index}
                      fontSize={{ sm: '14px' }}
                      width={cellWidth}
                      borderColor="transparent"
                    >
                      {data}
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Card>
  );
}
